"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.calculateRoomsState = exports.calculateTravellersDistributionWhenRoomsDecreasing = exports.calculateMaxRoomsCount = exports.calculateMinRoomsCount = exports.calculateMinCountOfTravellers = exports.calculateMaxCountOfTravellers = void 0;
const lodash_1 = require("lodash");
const noop_1 = __importDefault(require("lodash/noop"));
const react_1 = require("react");
const libs_hooks_react_1 = require("@hotelplan/libs.hooks-react");
const libs_tracking_1 = require("@hotelplan/libs.tracking");
const TravelRooms_utils_1 = require("./TravelRooms.utils");
const calculateMaxCountOfTravellers = (adults, childrenCount, maxTravellers) => {
    return {
        maxAdultsCount: maxTravellers - childrenCount,
        // at least 2 adults needed for 2 rooms -> 1 adult + 5 max children
        maxChildrenCount: (adults < 2 ? TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM : maxTravellers) - adults,
    };
};
exports.calculateMaxCountOfTravellers = calculateMaxCountOfTravellers;
const calculateMinCountOfTravellers = (rooms, isManualDistribution) => {
    return {
        minAdultsCount: isManualDistribution ? 1 : rooms !== 0 ? rooms : 1,
        minChildrenCount: 0,
    };
};
exports.calculateMinCountOfTravellers = calculateMinCountOfTravellers;
const calculateMinRoomsCount = (travellersCount) => {
    if (travellersCount > TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM * 2) {
        return 3;
    }
    else if (travellersCount > TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM) {
        return TravelRooms_utils_1.MAX_ROOMS_COUNT_DEFAULT;
    }
    else {
        return 1;
    }
};
exports.calculateMinRoomsCount = calculateMinRoomsCount;
const calculateMaxRoomsCount = (numberOfAdults, isSearchForFourRoomsEnabled) => {
    if (isSearchForFourRoomsEnabled)
        return TravelRooms_utils_1.FOUR_MAX_ROOMS_COUNT;
    return numberOfAdults === 1 ? 1 : TravelRooms_utils_1.MAX_ROOMS_COUNT_DEFAULT;
};
exports.calculateMaxRoomsCount = calculateMaxRoomsCount;
function calculateTravelDistribution(state, addRoom = false, options) {
    const { travellersDistribution } = state;
    if (!(travellersDistribution === null || travellersDistribution === void 0 ? void 0 : travellersDistribution.rooms.length)) {
        return {
            rooms: calculateB2CTravelersDistributionRooms(state, options),
        };
    }
    if (options.isSearchForFourRoomsEnabled && addRoom) {
        return {
            rooms: [
                ...travellersDistribution.rooms,
                {
                    adultsAmount: 1,
                    childrenIdx: [],
                },
            ],
        };
    }
    if (addRoom && travellersDistribution.rooms.length < 2) {
        return {
            rooms: [
                // First room
                {
                    adultsAmount: travellersDistribution.rooms[0].adultsAmount,
                    childrenIdx: travellersDistribution.rooms[0].childrenIdx,
                },
                // Second room
                {
                    adultsAmount: 1,
                    childrenIdx: [],
                },
            ],
        };
    }
    return travellersDistribution;
}
function distributeTravellersIntoRooms(rooms, adults, children) {
    const result = Array.from({ length: rooms }).map(() => ({
        adultsAmount: 1,
        childrenIdx: [],
    }));
    let remainingAdults = adults - rooms;
    let remainingChildrenIndexes = Array.from({ length: children }).map((_, index) => index + 1);
    for (let i = 0; i < result.length; i++) {
        const room = result[i];
        const availableRoomSpace = TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM - room.adultsAmount;
        const adultsToAdd = Math.min(remainingAdults, availableRoomSpace);
        room.adultsAmount = room.adultsAmount + adultsToAdd;
        remainingAdults = remainingAdults - adultsToAdd;
        if (TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM - room.adultsAmount > 0) {
            const possibleChildrenCountToAdd = Math.min(remainingChildrenIndexes.length, TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM - room.adultsAmount);
            room.childrenIdx = remainingChildrenIndexes.slice(0, possibleChildrenCountToAdd);
            remainingChildrenIndexes = remainingChildrenIndexes.slice(possibleChildrenCountToAdd);
        }
    }
    return result;
}
function calculateB2CTravelersDistributionRooms(state, options, nextValue) {
    const totalAdults = nextValue || state.adults;
    if (options.isSearchForFourRoomsEnabled) {
        return distributeTravellersIntoRooms(state.rooms, totalAdults, state.childrenDobs.length);
    }
    return [...Array(state.rooms)].map((_, index) => ({
        adultsAmount: state.rooms < 2 ? totalAdults : index === 0 ? totalAdults - 1 : 1,
        // Children calculation on BE starts with 1 (that's why '+ 1', to be consistent with BE)
        childrenIdx: state.childrenDobs.map((__, i) => i + 1),
    }));
}
const calculateRoomsDependsOnTravelersCount = (stateRoomsCount, travellersCount) => {
    const minRooms = (0, exports.calculateMinRoomsCount)(travellersCount);
    if (minRooms > stateRoomsCount) {
        return minRooms;
    }
    else {
        return stateRoomsCount;
    }
};
const calculateTravellersDistributionWhenRoomsDecreasing = (travellersDistribution) => {
    const rooms = [...travellersDistribution];
    const lastIndex = rooms.length - 1;
    const lastObj = rooms[lastIndex];
    // Distribute the adults and children to the previous objects
    for (let i = lastIndex - 1; i >= 0; i--) {
        const prevObj = rooms[i];
        const prevTotal = prevObj.adultsAmount + prevObj.childrenIdx.length;
        // Check if the previous object has space for additional adults and children
        if (prevTotal < TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM) {
            // Calculate the remaining space in the previous object
            const remainingSpace = TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM - prevTotal;
            // Calculate the amount of children to be added to the previous object
            const childrenToAdd = Math.min(lastObj.childrenIdx.length, remainingSpace);
            if (childrenToAdd) {
                // Update the values of the previous object
                prevObj.childrenIdx = [
                    ...prevObj.childrenIdx,
                    ...lastObj.childrenIdx.slice(0, childrenToAdd),
                ];
                // Update the values of the last object
                lastObj.childrenIdx =
                    lastObj.childrenIdx.length === childrenToAdd
                        ? []
                        : lastObj.childrenIdx.slice(childrenToAdd);
            }
            const adultsToAdd = Math.min(lastObj.adultsAmount, TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM -
                (prevObj.childrenIdx.length + prevObj.adultsAmount));
            if (adultsToAdd) {
                prevObj.adultsAmount = prevObj.adultsAmount + adultsToAdd;
                lastObj.adultsAmount = lastObj.adultsAmount - adultsToAdd;
            }
            // If the last object is empty, remove it from the array
            if (lastObj.adultsAmount === 0 && lastObj.childrenIdx.length === 0) {
                rooms.pop();
                break;
            }
        }
    }
    return { rooms: [...rooms] };
};
exports.calculateTravellersDistributionWhenRoomsDecreasing = calculateTravellersDistributionWhenRoomsDecreasing;
const calculateRoomsState = (state, options) => {
    const { rooms, adults, childrenDobs } = state;
    const childrenCount = childrenDobs.length;
    const travellersCount = adults + childrenCount;
    const isAddNewRoomForTravellersDistribution = options.disableRoomAdding
        ? false
        : (travellersCount === (options.isManualDistribution ? 6 : 7) &&
            rooms === 1) ||
            (travellersCount === (options.isManualDistribution ? 12 : 13) &&
                rooms === 2);
    return Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, state), { adults: isAddNewRoomForTravellersDistribution && options.isManualDistribution
            ? adults + 1
            : adults }), (0, exports.calculateMaxCountOfTravellers)(isAddNewRoomForTravellersDistribution && rooms !== 0
        ? adults + 1
        : adults, childrenCount, options.isSearchForFourRoomsEnabled
        ? TravelRooms_utils_1.MAX_TRAVELLERS_FOR_FOUR_ROOMS
        : TravelRooms_utils_1.MAX_TRAVELLERS_DEFAULT)), { minRoomsCount: state.rooms === 0
            ? 1
            : (0, exports.calculateMinRoomsCount)(isAddNewRoomForTravellersDistribution
                ? travellersCount + 1
                : travellersCount), maxRoomsCount: (0, exports.calculateMaxRoomsCount)(adults, options.isSearchForFourRoomsEnabled), rooms: state.rooms === 0
            ? state.rooms
            : calculateRoomsDependsOnTravelersCount(rooms, isAddNewRoomForTravellersDistribution
                ? travellersCount + 1
                : travellersCount) }), (0, exports.calculateMinCountOfTravellers)(rooms, options.isManualDistribution)), { travellersDistribution: calculateTravelDistribution(state, isAddNewRoomForTravellersDistribution, options) });
};
exports.calculateRoomsState = calculateRoomsState;
const useTravelRooms = (roomsProps, options) => {
    const [state, setRoomsState,] = (0, libs_hooks_react_1.useStateWithCallback)((0, exports.calculateRoomsState)(roomsProps, options), newState => {
        (0, libs_tracking_1.trackTravelRooms)({
            type: libs_tracking_1.TravelRoomsEventType.TRAVEL_ROOMS_UPDATE,
            payload: {
                adults: newState.adults,
                rooms: newState.rooms,
                children: newState.childrenDobs,
            },
        });
    });
    // NOTE: sort children dobs only when component mounts
    (0, react_1.useEffect)(() => {
        setRoomsState(m => (Object.assign(Object.assign({}, m), { childrenDobs: m.childrenDobs })), noop_1.default);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const changeAdultsCount = (nextValue, roomIndex) => {
        var _a;
        // For manual distribution adults is sum of adultsAmount in every room
        let totalAdults = 0;
        const updatedRooms = (_a = state.travellersDistribution.rooms) === null || _a === void 0 ? void 0 : _a.map((room, index) => {
            const localRoom = Object.assign({}, room);
            // Update data for specific room
            if (index === roomIndex) {
                localRoom.adultsAmount = nextValue;
            }
            totalAdults += localRoom.adultsAmount;
            return localRoom;
        });
        setRoomsState((0, exports.calculateRoomsState)(Object.assign(Object.assign({}, state), { adults: roomIndex !== undefined ? totalAdults : nextValue, travellersDistribution: {
                rooms: roomIndex !== undefined
                    ? updatedRooms
                    : calculateB2CTravelersDistributionRooms(state, options, nextValue),
            } }), options));
    };
    const addChild = (roomIndex) => {
        var _a;
        const updatedRooms = (_a = state.travellersDistribution.rooms) === null || _a === void 0 ? void 0 : _a.map((room, index) => {
            // Update data for specific room
            if (index === roomIndex ||
                (roomIndex === undefined &&
                    room.childrenIdx.length + room.adultsAmount <
                        TravelRooms_utils_1.MAX_TRAVELERS_FOR_ONE_ROOM)) {
                // On a backend side children calculation starts with 1
                // In case of loosing actuality remove '+ 1'
                return Object.assign(Object.assign({}, room), { childrenIdx: [...room.childrenIdx, state.childrenDobs.length + 1] });
            }
            return room;
        });
        setRoomsState((0, exports.calculateRoomsState)(Object.assign(Object.assign({}, state), { childrenDobs: [...state.childrenDobs, ''], travellersDistribution: {
                rooms: updatedRooms,
            } }), options));
    };
    const removeChild = (roomIndex) => {
        var _a;
        // When we delite children in room we need to delite specific DOB in childrenDobs by index
        let removableIndex;
        // In first .map() we choosing removable index
        const updatedRooms = (_a = state.travellersDistribution.rooms) === null || _a === void 0 ? void 0 : _a.map((room, index) => {
            // Updating data for specific room
            if (index === roomIndex) {
                // We always delete last children in room
                removableIndex = (0, lodash_1.last)(room.childrenIdx);
                return Object.assign(Object.assign({}, room), { childrenIdx: room.childrenIdx.slice(0, room.childrenIdx.length - 1) });
            }
            return room;
        }).map(room => (Object.assign(Object.assign({}, room), { childrenIdx: room.childrenIdx.map(childrenIndex => childrenIndex > removableIndex ? childrenIndex - 1 : childrenIndex) })));
        setRoomsState((0, exports.calculateRoomsState)(Object.assign(Object.assign({}, state), { childrenDobs: roomIndex === undefined
                ? state.childrenDobs.slice(0, state.childrenDobs.length - 1)
                : state.childrenDobs.filter(
                // Child calculation
                (_, index) => index !== removableIndex - 1), travellersDistribution: {
                rooms: updatedRooms,
            } }), options));
    };
    const changeChildrenCount = (nextValue, roomIndex) => {
        var _a;
        const currentChildrenCount = roomIndex !== undefined
            ? (_a = state.travellersDistribution) === null || _a === void 0 ? void 0 : _a.rooms[roomIndex].childrenIdx.length
            : state.childrenDobs.length;
        if (nextValue > currentChildrenCount) {
            return addChild(roomIndex);
        }
        if (nextValue < currentChildrenCount) {
            return removeChild(roomIndex);
        }
    };
    const changeCountOfRooms = nextValue => {
        const travellersDistribution = nextValue > state.rooms
            ? calculateTravelDistribution(state, true, options)
            : state.rooms > 1
                ? options.isSearchForFourRoomsEnabled
                    ? (0, exports.calculateTravellersDistributionWhenRoomsDecreasing)(state.travellersDistribution.rooms)
                    : {
                        rooms: [
                            {
                                // Moving all childrens and adults in one(1st) room
                                adultsAmount: state.adults,
                                // Children calculation on BE starts with 1 (that's why '+ 1')
                                childrenIdx: state.childrenDobs.map((_, index) => index + 1),
                            },
                        ],
                    }
                : { rooms: [] };
        const calculateAdults = () => {
            if (nextValue > state.adults) {
                return nextValue;
            }
            else if (options.isManualDistribution) {
                if (nextValue < state.rooms) {
                    return state.adults;
                }
                return state.adults + 1;
            }
            else {
                return state.adults;
            }
        };
        setRoomsState((0, exports.calculateRoomsState)(Object.assign(Object.assign(Object.assign({}, state), { rooms: nextValue, travellersDistribution }), (options.isSearchForFourRoomsEnabled
            ? {
                adults: calculateAdults(),
            }
            : {})), Object.assign(Object.assign({}, options), { disableRoomAdding: nextValue < state.rooms })));
    };
    const changeDOB = (childIndex, roomIndex) => newDob => {
        var _a, _b;
        // childrenIdx contains ids of childrenDobs for every particular room
        // Here we taking specific DOB in specific room;
        // Children calculation on BE starts with 1
        // And we increment childrenIdx (that's why '- 1')
        const childrenIdxInSpecificRoom = ((_b = (_a = state.travellersDistribution.rooms) === null || _a === void 0 ? void 0 : _a[roomIndex]) === null || _b === void 0 ? void 0 : _b.childrenIdx[childIndex]) -
            1;
        const checkingIndex = roomIndex !== undefined ? childrenIdxInSpecificRoom : childIndex;
        setRoomsState(Object.assign(Object.assign({}, state), { childrenDobs: state.childrenDobs.map((childDob, i) => i === checkingIndex ? newDob : childDob) }));
    };
    return {
        state,
        changeAdultsCount,
        changeChildrenCount,
        changeCountOfRooms,
        changeDOB,
    };
};
exports.default = useTravelRooms;
//# sourceMappingURL=TravelRooms.model.js.map